import React, { useEffect, useRef, useState } from 'react';

import { API_BASE_URL, UPLOADS_BASE_URL } from '../../config.js';
import '../EmployeeFeedbackChat.css';

const profileBase = UPLOADS_BASE_URL + '/uploads/profile/';

/* Chat avatar, shown beside incoming messages only - the way a group chat
   marks the other party. Plenty of the older profile_picture files are no
   longer on the server, so a failed load falls back to a letter: 'C' for a
   CFO message, otherwise the sender's initial. */
const Avatar = ({ name, picture, sender }) => {
  const [broken, setBroken] = useState(false);

  const isCfo = sender === 'cfo';
  // management is identified by role, so the tooltip matches the label
  const label = isCfo ? 'CFO' : name;

  if (!picture || broken) {
    return (
      <span
        className={
          'efb-avatar efb-avatar-fallback ' +
          (isCfo ? 'efb-avatar-cfo' : 'efb-avatar-employee')
        }
        title={label}
      >
        {isCfo ? 'C' : (name || '?').charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={`${profileBase}${picture}`}
      alt={label}
      title={label}
      className="efb-avatar"
      onError={() => setBroken(true)}
    />
  );
};

/* update_on is 'Y-m-d H:i:s' and applied_on is just 'Y-m-d', so show a clock
   time when there is one and fall back to the date otherwise. */
const formatStamp = (value) => {
  if (!value) return '';

  const parts = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(value);
  if (!parts) return value;

  if (parts[4] === undefined) {
    return `${parts[3]}/${parts[2]}/${parts[1]}`;
  }

  const hours = parseInt(parts[4], 10);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 || 12}:${parts[5]} ${suffix}`;
};

/**
 * The conversation on a single feedback entry, shared by the employee screen
 * and the CFO screen.
 *
 * viewer  - 'employee' or 'cfo'; decides which side each bubble sits on.
 * footer  - the composer to render under the thread (differs per screen).
 */
const FeedbackChatBox = ({ thread, loading, viewer, footer }) => {
  const messageEndRef = useRef(null);

  // keep the newest message in view as the thread loads or grows
  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [thread]);

  if (loading && !thread) {
    return <div className="efb-empty">Loading conversation...</div>;
  }

  if (!thread) {
    return <div className="efb-empty">Conversation unavailable</div>;
  }

  const bubble = (key, sender, name, picture, text, stamp) => {
    const mine = sender === viewer;

    return (
      <div
        key={key}
        className={'efb-row ' + (mine ? 'efb-row-end' : 'efb-row-start')}
      >
        {!mine && <Avatar name={name} picture={picture} sender={sender} />}

        <div className={'efb-bubble ' + (mine ? 'efb-out' : 'efb-in')}>
          {/* only the other party is named, as in a group chat */}
          {!mine && (
            <span
              className={
                'efb-sender-name ' +
                (sender === 'cfo' ? 'efb-name-cfo' : 'efb-name-employee')
              }
            >
              {sender === 'cfo' ? 'CFO' : name}
            </span>
          )}

          <span className="efb-text">{text}</span>
          <span className="efb-time">{formatStamp(stamp)}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="efb-category-bar">{thread.feedback.category_name}</div>

      <div className="efb-chat-container">
        <div className="efb-name-chip">{thread.feedback.employee_name}</div>

        <div className="efb-message-container">
          {/* the original feedback opens the thread */}
          {bubble(
            'origin',
            'employee',
            thread.feedback.employee_name,
            thread.feedback.profile_picture,
            thread.feedback.reason,
            thread.feedback.applied_on
          )}

          {thread.messages.map((msg, index) =>
            bubble(
              index,
              msg.sender,
              msg.sender_name,
              msg.profile_picture,
              msg.reason,
              msg.update_on
            )
          )}

          <div ref={messageEndRef} />
        </div>

        {footer}
      </div>
    </>
  );
};

export default FeedbackChatBox;
